<template>
  <a-layout class="responsible-gaming-page">
    <TitleBreadcrumb v-if="!isMobile" />
    <div class="content-base container">
      <!-- ADVISE MESSAGE: Spanish -->
      <div class="responsible-gaming-page-header">
        <h2 class="responsible-gaming-page-title">{{ labels.title }}</h2>
        <p class="responsible-gaming-page-subtitle">{{ labels.subtitle }}</p>
      </div>
      <a-tabs
        v-if="!isAutoExcluded"
        default-active-key="play-time-limit"
        :tabPosition="isMobile ? 'top' : 'left'"
      >
        <a-tab-pane key="play-time-limit">
          <span slot="tab">
            <font-awesome-icon
              :icon="['far', 'calendar-days']"
              class="anticon"
            />
            {{ labels.playTimeLimitForm.title }}
          </span>
          <play-amount-limit-form :labels="labels.playTimeLimitForm" />
        </a-tab-pane>
        <a-tab-pane key="self-exclusion">
          <span slot="tab">
            <font-awesome-icon :icon="['fas', 'ban']" class="anticon" />
            {{ labels.selfExclusionForm.title }}
          </span>
          <self-exclusion-form :labels="labels.selfExclusionForm" />
        </a-tab-pane>
      </a-tabs>
      <div v-else>
        <a-alert
          type="warning"
          style="margin-top: 12px; margin-bottom: 12px"
          show-icon
          :message="labels.autoExclusionMessage"
          :description="labels.autoExclusionDescription"
        />
      </div>
    </div>
  </a-layout>
</template>
<script>
import TitleBreadcrumb from "@/components/layout/client/TitleBreadcrumb";
import loadingMixin from "@/mixins/loadingMixin";
import { mapState, mapGetters } from "vuex";
import PlayAmountLimitForm from "./components/PlayAmountLimitForm.vue";
import SelfExclusionForm from "./components/SelfExclusionForm.vue";
import labels from "@/utils/labels.js";
export default {
  name: "ResponsibleGaming",
  components: {
    TitleBreadcrumb,
    PlayAmountLimitForm,
    SelfExclusionForm,
  },
  mixins: [loadingMixin],
  computed: {
    ...mapGetters({
      loggedUser: "clientAuth/getLoggedUser",
    }),
    ...mapState({
      screenWidth: (state) => state.layout.screenWidth,
    }),
    isAutoExcluded() {
      return this.loggedUser ? this.loggedUser.ise : false;
    },
    isMobile() {
      return this.screenWidth <= 768;
    },
  },
  data: () => ({
    labels: labels.responsibleGaming,
  }),
};
</script>
<style lang="scss" scoped>
@import "~@/assets/scss/variable";
.responsible-gaming-page {
  color: var(--text);
  background-color: transparent;
  &-header {
    padding: 0 24px;
  }
  &-title {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 8px;
    border-bottom: 1px solid var(--border-color);
    color: var(--text);
    @media screen and (max-width: $screen-mobile) {
      font-size: 20px;
    }
  }
  &-subtitle {
    font-size: 16px;
    margin-bottom: 24px;
    color: var(--text);
    @media screen and (max-width: $screen-mobile) {
      font-size: 14px;
    }
  }
  .content-base {
    margin-top: 12px;
    padding-top: 42px;
    padding-bottom: 42px;
    border-radius: 8px;
    border: 1px solid var(--card-shop-shadow);
    background-color: var(--card-shop-bg);
    @media screen and (max-width: $screen-mobile) {
      margin-top: 14px;
      max-width: calc(100% - 1.5rem) !important;
      margin-left: 0.75rem;
      margin-right: 0.75rem;
    }
  }
}
::v-deep .ant-form label {
  color: var(--text);
  font-family: "Roboto", sans-serif;
}
::v-deep .ant-input-number,
::v-deep .ant-input {
  background-color: var(--input-bg);
  color: var(--text);
  &:active,
  &:focus,
  &:hover {
    border-color: var(--text-active);
  }
}
::v-deep .ant-input-affix-wrapper {
  .ant-input:not(:first-child) {
    padding-left: 2.8125rem;
  }
  .ant-input-prefix {
    color: var(--text);
    font-size: 1.375rem;
    font-family: "Roboto", sans-serif;
  }
  .ant-input-clear-icon,
  .ant-input-textarea-clear-icon,
  .ant-input-password-icon {
    color: var(--text);
  }
}
::v-deep .has-error .ant-input-affix-wrapper .ant-input,
::v-deep .has-error .ant-input-affix-wrapper .ant-input:hover {
  background-color: color(c-primary);
}
::v-deep .ant-select {
  .ant-select-selection--single {
    color: var(--text);
    background-color: var(--input-bg);
    border: 1px solid var(--text-active);
    &:active,
    &:focus,
    &:hover {
      border-color: var(--text-active);
    }
    .anticon-down {
      color: var(--text);
    }
  }
}
</style>
<style lang="scss">
@import "~@/assets/scss/variable";

.success-modal {
  font-size: 16px;
  .ant-modal-content {
    background-color: var(--modal-bg) !important;
    box-shadow: 0 1px 3px 3px var(--modal-shadow);
    .ant-modal-confirm-title {
      color: var(--text) !important;
    }
    .ant-modal-confirm-content {
      color: var(--text) !important;
      p {
        font-size: 16px;
        text-align: justify;
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
    .ant-modal-confirm-btns {
      .ant-btn {
        background-color: var(--btn-bg-secondary) !important;
        border-color: var(--btn-bc-secondary) !important;
        color: var(--btn-text-secondary) !important;
        &:hover,
        &:focus,
        &:active {
          background-color: var(--btn-bg-secondary) !important;
          border-color: var(--btn-bc-secondary) !important;
          color: var(--btn-text-secondary) !important;
        }
        &.ant-btn-primary {
          background-color: var(--btn-bg-primary) !important;
          border-color: var(--btn-bc-primary) !important;
          color: var(--btn-text-primary) !important;
          &:hover {
            background-color: var(--btn-bg-primary) !important;
            border-color: var(--btn-bc-primary) !important;
            color: var(--btn-text-primary) !important;
          }
        }
      }
    }
  }
}

.ant-tabs-nav {
  .ant-tabs-tab {
    color: var(--text);
    &:hover {
      color: var(--text-active);
    }
    &.ant-tabs-tab-active {
      color: var(--text-active);
    }
  }
}
</style>
